import {
  CreateTransient,
  theme as defaultTheme,
  Spinner,
} from "@mindlercare/ui-web";
import MaterialDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions/DialogActions";
import DialogContent from "@mui/material/DialogContent/DialogContent";
import { PropsWithChildren } from "react";
import { styled, css } from "styled-components";
import { Typography } from "components/typography";
import { ButtonProps, Props } from "./customModal.types";

export const DialogHeaderContainer = styled.div<Pick<Props, "size">>`
  display: grid;
  grid-template-columns: auto auto;
  color: ${defaultTheme.COLORS.GRAY_BLACK};
  font-size: ${defaultTheme.FONT.SIZE.LARGE};
  font-weight: ${defaultTheme.FONT.WEIGHT.MEDIUM};
  padding: 24px 40px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

export const DialogTitleText = styled.h4`
  margin: 0;
`;

export const CloseIcon = styled.span`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 0;
  cursor: pointer;
`;

export const CloseDialog = styled(DialogActions)`
  margin: 0;
  padding: 0;
`;

export const DialogContentWrapperScrollable = styled.div<{
  isScrollable?: boolean;
}>`
  padding: 24px 40px 40px;
  overflow-y: auto;
`;

export const DialogContentWrapperStatic = styled.div<
  CreateTransient<{ hasTitle?: boolean }>
>`
  padding: ${({ $hasTitle }) => ($hasTitle ? "0" : "40px")} 40px 40px 40px;
  overflow-y: visible;
`;

export const DialogContentWrapper = ({ isScrollable = false, ...rest }) => {
  if (isScrollable) return <DialogContentWrapperScrollable {...rest} />;
  return <DialogContentWrapperStatic {...rest} />;
};

export const DialogContainer = styled(MaterialDialog)<Pick<Props, "size">>`
  margin: 0 auto;
  min-width: 564px;
  max-width: 100%;
  .MuiDialog-paperScrollPaper {
    max-height: 100%;
    overflow-y: visible;
    width: 100%;
    max-width: 564px;
    border-radius: 0px;
  }
`;

export const StyledDialogTitle = styled.div`
  padding: 0px 0px;
  margin-bottom: 9px;
`;

export const DialogHeaderTextContainer = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const DialogTitleTextSmall = styled.h4`
  margin: 0;
  font-weight: 700;
  font-size: 18px;
  padding: 32px 0 0 24px;
`;

export const DialogContentWrapperSmall = styled(DialogContent)`
  overflow-y: hidden;
  max-width: 396px;
  padding: 10px 24px 30px;
`;

export const DialogContainerSmall = styled(MaterialDialog)`
  min-width: 250px;
`;

export const DialogContainerMedium = styled(MaterialDialog)`
  .MuiDialog-paperScrollPaper {
    max-height: 100%;
    overflow-y: visible;
    width: 100%;
    max-width: 65vw;
    border-radius: 0px;
  }
`;

export const DialogHeaderTextSmall = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const Row = styled.div<{ noPadding?: boolean; paddingTop?: number }>`
  display: flex;
  padding-top: ${(p) => (p.noPadding ? 0 : "24px")};
  flex-wrap: wrap;
  width: 100%;
  ${({ paddingTop }) => paddingTop && `padding-top: ${paddingTop}px;`}
`;

export const Column = styled.div<{ fullWidth?: boolean; noMargin?: boolean }>`
  flex: ${(p) => (p.fullWidth ? "0 0 100%" : "0 0 50%")};
  margin-bottom: ${(p) => (p.noMargin ? "0" : "24px")};
`;

export const ColumnHeader = styled.p`
  color: ${(p) => defaultTheme.COLORS.GRAY_DARK};
  font-size: ${(p) => defaultTheme.FONT.SIZE.SMALL};
  font-weight: ${(p) => defaultTheme.FONT.WEIGHT.SEMIBOLD};
  font-family: ${(p) => defaultTheme.FONT.FAMILY.BODY};
  margin: 0 0 8px 0;
`;

export const ButtonRow = styled.div<{
  paddingTop?: number;
  marginTop?: number;
}>`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  ${({ marginTop }) => marginTop && `margin-top: ${marginTop}px;`}
  ${({ paddingTop }) => paddingTop && `padding-top: ${paddingTop}px;`}

  button:last-of-type {
    margin-left: 10px;
  }
`;

export const Paragraph = styled.p`
  margin: 0 0 24px 0;
  color: ${({ theme }) => defaultTheme.COLORS.GRAY_DARK};
  font-weight: ${({ theme }) => defaultTheme.FONT.WEIGHT.REGULAR};
`;

export const DropDownText = styled(Typography)`
  margin-bottom: 6px;
`;

export const SwitchDropdownDiv = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ActionButton = styled.button<ButtonProps>`
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  display: flex;
  padding: 0 24px;
  height: 40px;
  border: 1px solid
    ${({ variant, theme }) =>
      variant === "primary"
        ? defaultTheme.COLORS.PRIMARY
        : defaultTheme.COLORS.GRAY_MEDIUM};
  font-family: ${({ theme }) => defaultTheme.FONT.FAMILY.BODY};
  font-weight: ${({ theme }) => defaultTheme.FONT.WEIGHT.MEDIUM};
  font-size: ${({ theme }) => defaultTheme.FONT.SIZE.STANDARD};
  border-radius: 20px;
  transition: 0.2s ease-in-out;
  color: ${({ theme }) => defaultTheme.COLORS.GRAY_BLACK};
  background-color: ${({ variant, theme }) =>
    variant === "primary"
      ? defaultTheme.COLORS.PRIMARY
      : defaultTheme.COLORS.WHITE};

  &:hover {
    background-color: ${({ variant, theme }) =>
      variant === "primary" ? defaultTheme.COLORS.PRIMARY_DARK : null};
    transition: 0.2 ease-in-out;
    border: 1px solid
      ${({ variant, theme }) =>
        variant === "primary"
          ? defaultTheme.COLORS.PRIMARY_DARK
          : defaultTheme.COLORS.GRAY};
  }

  ${(p) =>
    p.disabled &&
    css`
      background-color: ${defaultTheme.COLORS.PRIMARY};
      opacity: 0.3;
      border: 1px solid ${defaultTheme.COLORS.PRIMARY};

      &:hover {
        background-color: ${defaultTheme.COLORS.PRIMARY};
        border: 1px solid ${defaultTheme.COLORS.PRIMARY};
      }
    `}
`;

const StyledSpinner = styled(Spinner)`
  scale: 75%;
`;

export const Button = ({
  text,
  spinning = false,
  ...props
}: PropsWithChildren<ButtonProps & { spinning?: boolean }>) => (
  <ActionButton text={text} {...props}>
    {spinning ? <StyledSpinner /> : text}
  </ActionButton>
);
